import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { useTheme } from '@/context/ThemeContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Component Section Wrapper
const Section = ({ title, description, children, id }) => (
  <section id={id} className="mb-8 lg:mb-12 scroll-mt-20">
    <div className="mb-4 lg:mb-6">
      <h2 className="text-xl lg:text-2xl font-semibold mb-1 lg:mb-2">{title}</h2>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
    {children}
  </section>
);

// Component Preview Box
const Preview = ({ title, children, className }) => (
  <div className={cn("border border-border rounded-lg overflow-hidden", className)}>
    {title && (
      <div className="px-3 lg:px-4 py-2 bg-muted/30 border-b border-border">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</span>
      </div>
    )}
    <div className="p-4 lg:p-6 flex flex-wrap items-center gap-3 lg:gap-4">
      {children}
    </div>
  </div>
);

// Code Block
const CodeBlock = ({ children }) => (
  <pre className="mt-3 p-4 bg-muted/50 rounded-lg overflow-x-auto text-xs font-mono">
    <code>{children}</code>
  </pre>
);

// Color Swatch
const ColorSwatch = ({ name, variable, className }) => (
  <div className="flex flex-col items-center gap-1 lg:gap-2">
    <div className={cn("w-12 h-12 lg:w-16 lg:h-16 rounded-lg border border-border shadow-sm", className)}></div>
    <div className="text-center">
      <p className="text-xs font-medium">{name}</p>
      <p className="text-[9px] lg:text-[10px] text-muted-foreground font-mono">{variable}</p>
    </div>
  </div>
);

// Navigation Item
const NavItem = ({ href, children, active }) => (
  <a 
    href={href} 
    className={cn(
      "block px-3 py-1.5 rounded text-sm transition-colors",
      active ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
    )}
  >
    {children}
  </a>
);

export const DesignSystem = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const navSections = [
    { id: 'overview', label: 'Overview' },
    { id: 'theming', label: 'Theming' },
    { id: 'colors', label: 'Colors' },
    { id: 'typography', label: 'Typography' },
    { id: 'buttons', label: 'Buttons' },
    { id: 'badges', label: 'Badges' },
    { id: 'cards', label: 'Cards' },
    { id: 'inputs', label: 'Form Inputs' },
    { id: 'selects', label: 'Selects' },
    { id: 'switches', label: 'Switches & Checkboxes' },
    { id: 'tabs', label: 'Tabs' },
    { id: 'tables', label: 'Tables' },
    { id: 'alerts', label: 'Alerts' },
    { id: 'progress', label: 'Progress' },
    { id: 'dragdrop', label: 'Drag & Drop' },
    { id: 'crypto', label: 'Crypto Components' },
    { id: 'icons', label: 'Icons' },
    { id: 'spacing', label: 'Spacing' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-4 lg:px-6 py-3 lg:py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 lg:gap-3">
            {/* Mobile menu button */}
            <button 
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              className="lg:hidden p-2 -ml-2 rounded-md hover:bg-muted"
            >
              <i className={`fa-solid ${mobileNavOpen ? 'fa-xmark' : 'fa-bars'} text-lg`}></i>
            </button>
            <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-lg overflow-hidden">
              <img src="/cryptox-icon.png" alt="CryptoX" className="w-full h-full object-cover" />
            </div>
            <div className="hidden sm:block">
              <h1 className="font-semibold text-sm lg:text-base">CryptoX Design System</h1>
              <p className="text-xs text-muted-foreground">v1.0.0</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-8 w-8 lg:h-9 lg:w-9"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? (
                <i className="fa-solid fa-sun text-sm lg:text-base"></i>
              ) : (
                <i className="fa-solid fa-moon text-sm lg:text-base"></i>
              )}
            </Button>
            <Badge variant="secondary" className="hidden md:inline-flex">Documentation</Badge>
            <a href="/CryptoX-Design-System.pdf" download className="hidden sm:inline-flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs lg:text-sm font-medium hover:bg-primary/90 transition-colors">
              <i className="fa-solid fa-file-pdf"></i>
              <span className="hidden lg:inline">PDF</span>
            </a>
            <a href="/cryptox-design-system-only.zip" download className="inline-flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 rounded-md bg-secondary text-secondary-foreground text-xs lg:text-sm font-medium hover:bg-secondary/80 transition-colors">
              <i className="fa-solid fa-file-zipper"></i>
              <span className="hidden sm:inline">ZIP</span>
            </a>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" onClick={() => setMobileNavOpen(false)}>
          <div className="fixed top-[57px] left-0 w-64 h-[calc(100vh-57px)] bg-card border-r border-border p-4 overflow-y-auto" onClick={e => e.stopPropagation()}>
            <nav className="space-y-1">
              {navSections.map((section) => (
                <a 
                  key={section.id} 
                  href={`#${section.id}`}
                  onClick={() => setMobileNavOpen(false)}
                  className={cn(
                    "block px-3 py-2 rounded text-sm transition-colors",
                    activeSection === section.id ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                >
                  {section.label}
                </a>
              ))}
            </nav>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto flex">
        {/* Sidebar Navigation - Desktop only */}
        <aside className="w-56 shrink-0 border-r border-border p-4 sticky top-[65px] h-[calc(100vh-65px)] overflow-y-auto hidden lg:block">
          <nav className="space-y-1">
            {navSections.map((section) => (
              <NavItem 
                key={section.id} 
                href={`#${section.id}`}
                active={activeSection === section.id}
              >
                {section.label}
              </NavItem>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-10 max-w-4xl overflow-x-hidden">
          
          {/* Overview */}
          <Section id="overview" title="Design System Overview" description="A comprehensive design system for cryptocurrency dashboards with dark/light mode support.">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="p-4 text-center">
                  <i className="fa-solid fa-palette text-2xl text-primary mb-2"></i>
                  <h3 className="font-medium">Semantic Colors</h3>
                  <p className="text-xs text-muted-foreground mt-1">HSL-based tokens for consistent theming</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <i className="fa-solid fa-moon text-2xl text-primary mb-2"></i>
                  <h3 className="font-medium">Dark/Light Mode</h3>
                  <p className="text-xs text-muted-foreground mt-1">Full theme support with CSS variables</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <i className="fa-solid fa-mobile-screen text-2xl text-primary mb-2"></i>
                  <h3 className="font-medium">Responsive</h3>
                  <p className="text-xs text-muted-foreground mt-1">Mobile-first responsive design</p>
                </CardContent>
              </Card>
            </div>

            <Preview title="Tech Stack">
              <Badge>React 19</Badge>
              <Badge>Tailwind CSS</Badge>
              <Badge>shadcn/ui</Badge>
              <Badge>Font Awesome</Badge>
              <Badge>Recharts</Badge>
              <Badge>@dnd-kit</Badge>
            </Preview>
          </Section>

          {/* Theming */}
          <Section id="theming" title="Theming" description="Full dark/light mode support with automatic theme persistence.">
            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4">Theme Toggle</h3>
            <Preview title="Switch Between Themes">
              <div className="w-full space-y-4">
                <p className="text-sm text-muted-foreground">
                  Click the sun/moon icon in the header to toggle between dark and light modes. 
                  Your preference is automatically saved to localStorage.
                </p>
                <div className="flex items-center gap-4">
                  <div className="p-4 rounded-lg bg-[#0f172a] text-white">
                    <i className="fa-solid fa-moon mr-2"></i>
                    Dark Mode
                  </div>
                  <div className="p-4 rounded-lg bg-[#f8fafc] text-[#0f172a] border border-gray-200">
                    <i className="fa-solid fa-sun mr-2"></i>
                    Light Mode
                  </div>
                </div>
              </div>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Implementation</h3>
            <CodeBlock>{`// ThemeContext.jsx
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('dark');
  
  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Usage
const { theme, toggleTheme } = useTheme();`}</CodeBlock>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">CSS Variables</h3>
            <Preview title="Theme Comparison">
              <div className="w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Token</TableHead>
                      <TableHead>Dark Mode</TableHead>
                      <TableHead>Light Mode</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-mono text-xs">--background</TableCell>
                      <TableCell><span className="inline-block w-4 h-4 rounded bg-[#0f172a] mr-2"></span>#0f172a</TableCell>
                      <TableCell><span className="inline-block w-4 h-4 rounded bg-[#f8fafc] border mr-2"></span>#f8fafc</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-mono text-xs">--card</TableCell>
                      <TableCell><span className="inline-block w-4 h-4 rounded bg-[#1e293b] mr-2"></span>#1e293b</TableCell>
                      <TableCell><span className="inline-block w-4 h-4 rounded bg-white border mr-2"></span>#ffffff</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-mono text-xs">--primary</TableCell>
                      <TableCell><span className="inline-block w-4 h-4 rounded bg-[#3b82f6] mr-2"></span>#3b82f6</TableCell>
                      <TableCell><span className="inline-block w-4 h-4 rounded bg-[#3b82f6] mr-2"></span>#3b82f6</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-mono text-xs">--border</TableCell>
                      <TableCell><span className="inline-block w-4 h-4 rounded bg-[#334155] mr-2"></span>#334155</TableCell>
                      <TableCell><span className="inline-block w-4 h-4 rounded bg-[#e2e8f0] border mr-2"></span>#e2e8f0</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </Preview>
          </Section>

          {/* Colors */}
          <Section id="colors" title="Colors" description="Semantic color tokens using HSL format for easy theming.">
            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4">Base Colors</h3>
            <Preview title="Primary & Secondary">
              <ColorSwatch name="Background" variable="--background" className="bg-background" />
              <ColorSwatch name="Foreground" variable="--foreground" className="bg-foreground" />
              <ColorSwatch name="Card" variable="--card" className="bg-card" />
              <ColorSwatch name="Primary" variable="--primary" className="bg-primary" />
              <ColorSwatch name="Secondary" variable="--secondary" className="bg-secondary" />
              <ColorSwatch name="Muted" variable="--muted" className="bg-muted" />
              <ColorSwatch name="Accent" variable="--accent" className="bg-accent" />
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">State Colors</h3>
            <Preview title="Success, Warning, Error">
              <ColorSwatch name="Success" variable="--success" className="bg-success" />
              <ColorSwatch name="Warning" variable="--warning" className="bg-warning" />
              <ColorSwatch name="Destructive" variable="--destructive" className="bg-destructive" />
              <ColorSwatch name="Info" variable="--info" className="bg-info" />
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Crypto Colors</h3>
            <Preview title="Cryptocurrency Specific">
              <ColorSwatch name="Bitcoin" variable="--bitcoin" className="bg-crypto-bitcoin" />
              <ColorSwatch name="Ethereum" variable="--ethereum" className="bg-crypto-ethereum" />
              <ColorSwatch name="Gain" variable="--gain" className="bg-crypto-gain" />
              <ColorSwatch name="Loss" variable="--loss" className="bg-crypto-loss" />
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Border & Input</h3>
            <Preview title="UI Elements">
              <ColorSwatch name="Border" variable="--border" className="bg-border" />
              <ColorSwatch name="Input" variable="--input" className="bg-input" />
              <ColorSwatch name="Ring" variable="--ring" className="bg-ring" />
            </Preview>

            <CodeBlock>{`/* CSS Variables - HSL Format */
:root {
  --primary: 221 83% 53%;
  --success: 142 71% 45%;
  --destructive: 0 84% 60%;
  --bitcoin: 38 92% 50%;
}

/* Usage */
.element {
  background-color: hsl(var(--primary));
  color: hsl(var(--primary-foreground));
}`}</CodeBlock>
          </Section>

          {/* Typography */}
          <Section id="typography" title="Typography" description="Inter font family with consistent sizing scale.">
            <Preview title="Font Family">
              <div className="w-full space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Sans (Inter)</p>
                  <p className="text-2xl">The quick brown fox jumps over the lazy dog</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Mono (SF Mono)</p>
                  <p className="text-2xl font-mono">$67,234.56 | 0.025 BTC</p>
                </div>
              </div>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Font Sizes</h3>
            <Preview title="Size Scale">
              <div className="w-full space-y-3">
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-muted-foreground w-16">text-xs</span>
                  <span className="text-xs">12px - Extra Small Text</span>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-muted-foreground w-16">text-sm</span>
                  <span className="text-sm">14px - Small Text</span>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-muted-foreground w-16">text-base</span>
                  <span className="text-base">16px - Base Text</span>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-muted-foreground w-16">text-lg</span>
                  <span className="text-lg">18px - Large Text</span>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-muted-foreground w-16">text-xl</span>
                  <span className="text-xl">20px - Extra Large</span>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-muted-foreground w-16">text-2xl</span>
                  <span className="text-2xl">24px - Heading</span>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-muted-foreground w-16">text-3xl</span>
                  <span className="text-3xl">30px - Large Heading</span>
                </div>
              </div>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Font Weights</h3>
            <Preview title="Weight Scale">
              <div className="w-full space-y-2">
                <p className="font-normal">400 - Normal: The quick brown fox</p>
                <p className="font-medium">500 - Medium: The quick brown fox</p>
                <p className="font-semibold">600 - Semibold: The quick brown fox</p>
                <p className="font-bold">700 - Bold: The quick brown fox</p>
              </div>
            </Preview>
          </Section>

          {/* Buttons */}
          <Section id="buttons" title="Buttons" description="Button components with multiple variants and sizes.">
            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4">Variants</h3>
            <Preview title="All Variants">
              <Button variant="default">Default</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="link">Link</Button>
              <Button variant="destructive">Destructive</Button>
              <Button variant="success">Success</Button>
              <Button variant="warning">Warning</Button>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Sizes</h3>
            <Preview title="Size Options">
              <Button size="sm">Small</Button>
              <Button size="default">Default</Button>
              <Button size="lg">Large</Button>
              <Button size="icon"><i className="fa-solid fa-plus"></i></Button>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">States</h3>
            <Preview title="Interactive States">
              <Button>Normal</Button>
              <Button className="hover:bg-primary/90">Hover</Button>
              <Button disabled>Disabled</Button>
              <Button>
                <i className="fa-solid fa-spinner animate-spin mr-2"></i>
                Loading
              </Button>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">With Icons</h3>
            <Preview title="Icon Buttons">
              <Button><i className="fa-solid fa-plus mr-2"></i>Add Asset</Button>
              <Button variant="outline"><i className="fa-solid fa-download mr-2"></i>Export</Button>
              <Button variant="secondary">Settings<i className="fa-solid fa-gear ml-2"></i></Button>
              <Button variant="ghost" size="icon"><i className="fa-solid fa-bell"></i></Button>
            </Preview>

            <CodeBlock>{`<Button variant="default">Default</Button>
<Button variant="secondary">Secondary</Button>
<Button variant="outline">Outline</Button>
<Button variant="destructive">Destructive</Button>
<Button size="sm">Small</Button>
<Button size="lg">Large</Button>
<Button disabled>Disabled</Button>`}</CodeBlock>
          </Section>

          {/* Badges */}
          <Section id="badges" title="Badges" description="Badge components for status indicators and labels.">
            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4">Variants</h3>
            <Preview title="All Variants">
              <Badge variant="default">Default</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="outline">Outline</Badge>
              <Badge variant="destructive">Destructive</Badge>
              <Badge variant="success">Success</Badge>
              <Badge variant="warning">Warning</Badge>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Crypto Badges</h3>
            <Preview title="Price Change Badges">
              <Badge variant="gain"><i className="fa-solid fa-arrow-up mr-1"></i>+5.67%</Badge>
              <Badge variant="loss"><i className="fa-solid fa-arrow-down mr-1"></i>-2.34%</Badge>
              <Badge variant="gain">+$1,234.56</Badge>
              <Badge variant="loss">-$567.89</Badge>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">With Icons</h3>
            <Preview title="Icon Badges">
              <Badge><i className="fa-brands fa-bitcoin mr-1"></i>BTC</Badge>
              <Badge variant="secondary"><i className="fa-brands fa-ethereum mr-1"></i>ETH</Badge>
              <Badge variant="success"><i className="fa-solid fa-check mr-1"></i>Completed</Badge>
              <Badge variant="warning"><i className="fa-solid fa-clock mr-1"></i>Pending</Badge>
              <Badge variant="destructive"><i className="fa-solid fa-xmark mr-1"></i>Failed</Badge>
            </Preview>

            <CodeBlock>{`<Badge variant="gain">
  <i className="fa-solid fa-arrow-up mr-1"></i>
  +5.67%
</Badge>
<Badge variant="loss">-2.34%</Badge>`}</CodeBlock>
          </Section>

          {/* Cards */}
          <Section id="cards" title="Cards" description="Card containers for grouping content.">
            <Preview title="Basic Card">
              <Card className="w-full max-w-sm">
                <CardHeader>
                  <CardTitle>Card Title</CardTitle>
                  <CardDescription>Card description text goes here</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">This is the card content area where you can place any content.</p>
                </CardContent>
              </Card>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Stats Card</h3>
            <Preview title="Dashboard Stats">
              <Card className="w-64">
                <CardContent className="p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-muted-foreground">Portfolio Value</p>
                      <p className="text-2xl font-semibold font-mono mt-1">$124,567</p>
                      <Badge variant="gain" className="mt-2">
                        <i className="fa-solid fa-arrow-up mr-1"></i>12.34%
                      </Badge>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <i className="fa-solid fa-wallet text-primary"></i>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="w-64">
                <CardContent className="p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-muted-foreground">24h Loss</p>
                      <p className="text-2xl font-semibold font-mono mt-1">-$1,234</p>
                      <Badge variant="loss" className="mt-2">
                        <i className="fa-solid fa-arrow-down mr-1"></i>2.34%
                      </Badge>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                      <i className="fa-solid fa-chart-line text-destructive"></i>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Preview>
          </Section>

          {/* Inputs */}
          <Section id="inputs" title="Form Inputs" description="Text input fields with various states.">
            <Preview title="Input States">
              <div className="w-full max-w-sm space-y-4">
                <div className="space-y-2">
                  <Label>Default Input</Label>
                  <Input placeholder="Enter text..." />
                </div>
                <div className="space-y-2">
                  <Label>With Value</Label>
                  <Input defaultValue="john@example.com" />
                </div>
                <div className="space-y-2">
                  <Label>Disabled</Label>
                  <Input placeholder="Disabled input" disabled />
                </div>
              </div>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Input Types</h3>
            <Preview title="Different Types">
              <div className="w-full max-w-sm space-y-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" placeholder="email@example.com" />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input type="password" placeholder="••••••••" />
                </div>
                <div className="space-y-2">
                  <Label>Number</Label>
                  <Input type="number" placeholder="0.00" />
                </div>
                <div className="space-y-2">
                  <Label>Search</Label>
                  <div className="relative">
                    <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm"></i>
                    <Input type="search" placeholder="Search..." className="pl-9" />
                  </div>
                </div>
              </div>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Crypto Input</h3>
            <Preview title="Amount Input">
              <div className="w-full max-w-sm space-y-2">
                <Label>Amount</Label>
                <div className="relative">
                  <Input type="number" placeholder="0.00" className="pl-8 pr-16 font-mono" />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">USD</span>
                </div>
              </div>
            </Preview>
          </Section>

          {/* Selects */}
          <Section id="selects" title="Select Dropdowns" description="Dropdown select components.">
            <Preview title="Select Examples">
              <div className="w-full max-w-sm space-y-4">
                <div className="space-y-2">
                  <Label>Select Crypto</Label>
                  <Select defaultValue="btc">
                    <SelectTrigger>
                      <SelectValue placeholder="Select coin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="btc">Bitcoin (BTC)</SelectItem>
                      <SelectItem value="eth">Ethereum (ETH)</SelectItem>
                      <SelectItem value="sol">Solana (SOL)</SelectItem>
                      <SelectItem value="ada">Cardano (ADA)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Time Period</Label>
                  <Select defaultValue="7d">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24h">24 Hours</SelectItem>
                      <SelectItem value="7d">7 Days</SelectItem>
                      <SelectItem value="30d">30 Days</SelectItem>
                      <SelectItem value="1y">1 Year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Preview>
          </Section>

          {/* Switches & Checkboxes */}
          <Section id="switches" title="Switches & Checkboxes" description="Toggle and checkbox components.">
            <Preview title="Switches">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Switch id="switch1" />
                  <Label htmlFor="switch1">Default Off</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch id="switch2" defaultChecked />
                  <Label htmlFor="switch2">Default On</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch id="switch3" disabled />
                  <Label htmlFor="switch3">Disabled</Label>
                </div>
              </div>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Checkboxes</h3>
            <Preview title="Checkbox States">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Checkbox id="check1" />
                  <Label htmlFor="check1">Unchecked</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox id="check2" defaultChecked />
                  <Label htmlFor="check2">Checked</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox id="check3" disabled />
                  <Label htmlFor="check3">Disabled</Label>
                </div>
              </div>
            </Preview>
          </Section>

          {/* Tabs */}
          <Section id="tabs" title="Tabs" description="Tab navigation components.">
            <Preview title="Default Tabs">
              <Tabs defaultValue="overview" className="w-full max-w-md">
                <TabsList>
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="analytics">Analytics</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="mt-4">
                  <p className="text-sm text-muted-foreground">Overview content goes here.</p>
                </TabsContent>
                <TabsContent value="analytics" className="mt-4">
                  <p className="text-sm text-muted-foreground">Analytics content goes here.</p>
                </TabsContent>
                <TabsContent value="settings" className="mt-4">
                  <p className="text-sm text-muted-foreground">Settings content goes here.</p>
                </TabsContent>
              </Tabs>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Time Range Tabs</h3>
            <Preview title="Chart Time Range">
              <Tabs defaultValue="7d">
                <TabsList className="h-8">
                  <TabsTrigger value="24h" className="text-xs px-3 h-7">24H</TabsTrigger>
                  <TabsTrigger value="7d" className="text-xs px-3 h-7">7D</TabsTrigger>
                  <TabsTrigger value="1m" className="text-xs px-3 h-7">1M</TabsTrigger>
                  <TabsTrigger value="3m" className="text-xs px-3 h-7">3M</TabsTrigger>
                  <TabsTrigger value="1y" className="text-xs px-3 h-7">1Y</TabsTrigger>
                  <TabsTrigger value="all" className="text-xs px-3 h-7">ALL</TabsTrigger>
                </TabsList>
              </Tabs>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Trade Tabs</h3>
            <Preview title="Buy/Sell Tabs">
              <Tabs defaultValue="buy" className="w-64">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="buy" className="data-[state=active]:bg-success data-[state=active]:text-success-foreground">Buy</TabsTrigger>
                  <TabsTrigger value="sell" className="data-[state=active]:bg-destructive data-[state=active]:text-destructive-foreground">Sell</TabsTrigger>
                </TabsList>
              </Tabs>
            </Preview>
          </Section>

          {/* Tables */}
          <Section id="tables" title="Tables" description="Data table components.">
            <Preview title="Basic Table">
              <div className="w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">24h</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-muted-foreground">1</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-crypto-bitcoin/15 flex items-center justify-center">
                            <i className="fa-brands fa-bitcoin text-crypto-bitcoin text-xs"></i>
                          </div>
                          <div>
                            <p className="font-medium">Bitcoin</p>
                            <p className="text-xs text-muted-foreground">BTC</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">$67,234.56</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="gain">+2.34%</Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">Trade</Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-muted-foreground">2</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-crypto-ethereum/15 flex items-center justify-center">
                            <i className="fa-brands fa-ethereum text-crypto-ethereum text-xs"></i>
                          </div>
                          <div>
                            <p className="font-medium">Ethereum</p>
                            <p className="text-xs text-muted-foreground">ETH</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">$3,245.78</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="loss">-1.23%</Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">Trade</Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </Preview>
          </Section>

          {/* Alerts */}
          <Section id="alerts" title="Alerts" description="Alert and notification components.">
            <Preview title="Alert Types">
              <div className="w-full space-y-4">
                <div className="p-4 rounded-lg bg-success/10 border border-success/20 flex items-start gap-3">
                  <i className="fa-solid fa-check text-success mt-0.5"></i>
                  <div>
                    <p className="font-medium text-success">Success</p>
                    <p className="text-sm text-muted-foreground">Your transaction was completed successfully.</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-3">
                  <i className="fa-solid fa-xmark text-destructive mt-0.5"></i>
                  <div>
                    <p className="font-medium text-destructive">Error</p>
                    <p className="text-sm text-muted-foreground">Transaction failed. Please try again.</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-warning/10 border border-warning/20 flex items-start gap-3">
                  <i className="fa-solid fa-triangle-exclamation text-warning mt-0.5"></i>
                  <div>
                    <p className="font-medium text-warning">Warning</p>
                    <p className="text-sm text-muted-foreground">High gas fees detected. Consider waiting.</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-info/10 border border-info/20 flex items-start gap-3">
                  <i className="fa-solid fa-circle-info text-info mt-0.5"></i>
                  <div>
                    <p className="font-medium text-info">Info</p>
                    <p className="text-sm text-muted-foreground">Market is currently volatile.</p>
                  </div>
                </div>
              </div>
            </Preview>
          </Section>

          {/* Progress */}
          <Section id="progress" title="Progress" description="Progress indicators and loading states.">
            <Preview title="Progress Bars">
              <div className="w-full max-w-md space-y-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>0%</span>
                    <span className="text-muted-foreground">Empty</span>
                  </div>
                  <Progress value={0} />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>33%</span>
                    <span className="text-muted-foreground">Low</span>
                  </div>
                  <Progress value={33} />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>66%</span>
                    <span className="text-muted-foreground">Medium</span>
                  </div>
                  <Progress value={66} />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>100%</span>
                    <span className="text-muted-foreground">Complete</span>
                  </div>
                  <Progress value={100} />
                </div>
              </div>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Slider</h3>
            <Preview title="Slider Control">
              <div className="w-full max-w-md space-y-4">
                <div className="space-y-2">
                  <Label>Risk Tolerance</Label>
                  <Slider defaultValue={[50]} max={100} step={1} />
                </div>
              </div>
            </Preview>
          </Section>

          {/* Drag & Drop */}
          <Section id="dragdrop" title="Drag & Drop" description="Customizable dashboard with drag-and-drop widget reordering.">
            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4">Dashboard Customization</h3>
            <Preview title="Edit Mode">
              <div className="w-full space-y-4">
                <p className="text-sm text-muted-foreground">
                  The dashboard supports drag-and-drop customization. Users can reorder widgets, 
                  show/hide sections, and their preferences are saved to localStorage.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">
                    <i className="fa-solid fa-grip-vertical mr-1"></i>
                    Drag to Reorder
                  </Badge>
                  <Badge variant="secondary">
                    <i className="fa-solid fa-eye mr-1"></i>
                    Show/Hide Widgets
                  </Badge>
                  <Badge variant="secondary">
                    <i className="fa-solid fa-floppy-disk mr-1"></i>
                    Auto-Save Layout
                  </Badge>
                </div>
              </div>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Available Widgets</h3>
            <Preview title="Dashboard Widgets">
              <div className="w-full grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { icon: 'fa-solid fa-chart-bar', name: 'Stats Cards' },
                  { icon: 'fa-solid fa-chart-line', name: 'Price Chart' },
                  { icon: 'fa-solid fa-briefcase', name: 'Portfolio' },
                  { icon: 'fa-solid fa-robot', name: 'AI Insights' },
                  { icon: 'fa-solid fa-bell', name: 'Alerts' },
                  { icon: 'fa-solid fa-table', name: 'Market Table' },
                  { icon: 'fa-solid fa-clock-rotate-left', name: 'Transactions' },
                ].map((widget) => (
                  <div key={widget.name} className="p-3 rounded-lg border border-border flex items-center gap-2">
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                      <i className={cn(widget.icon, "text-primary text-sm")}></i>
                    </div>
                    <span className="text-sm font-medium">{widget.name}</span>
                  </div>
                ))}
              </div>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Implementation</h3>
            <CodeBlock>{`// Using @dnd-kit for drag and drop
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';

const SortableWidget = ({ id }) => {
  const { attributes, listeners, setNodeRef, transform } = useSortable({ id });
  
  return (
    <div ref={setNodeRef} style={{ transform }} {...attributes} {...listeners}>
      <Widget id={id} />
    </div>
  );
};

// Dashboard with customization
const Dashboard = () => {
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setLayout(items => arrayMove(items, oldIndex, newIndex));
    }
  };

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <SortableContext items={layout}>
        {layout.map(id => <SortableWidget key={id} id={id} />)}
      </SortableContext>
    </DndContext>
  );
};`}</CodeBlock>
          </Section>

          {/* Crypto Components */}
          <Section id="crypto" title="Crypto Components" description="Cryptocurrency-specific UI components.">
            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4">Crypto Icons</h3>
            <Preview title="Coin Icons">
              <div className="w-8 h-8 rounded-full bg-crypto-bitcoin/15 flex items-center justify-center">
                <i className="fa-brands fa-bitcoin text-crypto-bitcoin"></i>
              </div>
              <div className="w-8 h-8 rounded-full bg-crypto-ethereum/15 flex items-center justify-center">
                <i className="fa-brands fa-ethereum text-crypto-ethereum"></i>
              </div>
              <div className="w-8 h-8 rounded-full bg-purple-500/15 flex items-center justify-center">
                <i className="fa-solid fa-sun text-purple-500"></i>
              </div>
              <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
                <i className="fa-solid fa-coins text-primary"></i>
              </div>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Transaction Icons</h3>
            <Preview title="Transaction Types">
              <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center">
                <i className="fa-solid fa-arrow-down text-success"></i>
              </div>
              <span className="text-sm">Buy</span>
              <div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center">
                <i className="fa-solid fa-arrow-up text-destructive"></i>
              </div>
              <span className="text-sm">Sell</span>
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <i className="fa-solid fa-arrow-right-arrow-left text-primary"></i>
              </div>
              <span className="text-sm">Swap</span>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Price Display</h3>
            <Preview title="Price Formats">
              <div className="space-y-2">
                <p className="text-2xl font-semibold font-mono">$67,234.56</p>
                <p className="text-sm text-muted-foreground">Large price (BTC)</p>
              </div>
              <div className="space-y-2">
                <p className="text-2xl font-semibold font-mono">$0.5234</p>
                <p className="text-sm text-muted-foreground">Small price (XRP)</p>
              </div>
              <div className="space-y-2">
                <p className="text-2xl font-semibold font-mono text-success">+$2,456.78</p>
                <p className="text-sm text-muted-foreground">Profit</p>
              </div>
              <div className="space-y-2">
                <p className="text-2xl font-semibold font-mono text-destructive">-$567.89</p>
                <p className="text-sm text-muted-foreground">Loss</p>
              </div>
            </Preview>
          </Section>

          {/* Icons */}
          <Section id="icons" title="Icons" description="Font Awesome icons used throughout the design system.">
            <Preview title="Navigation Icons">
              <div className="flex items-center gap-2"><i className="fa-solid fa-house w-5 text-center"></i><span className="text-sm">Dashboard</span></div>
              <div className="flex items-center gap-2"><i className="fa-solid fa-briefcase w-5 text-center"></i><span className="text-sm">Portfolio</span></div>
              <div className="flex items-center gap-2"><i className="fa-solid fa-clock-rotate-left w-5 text-center"></i><span className="text-sm">Transactions</span></div>
              <div className="flex items-center gap-2"><i className="fa-solid fa-chart-line w-5 text-center"></i><span className="text-sm">Markets</span></div>
              <div className="flex items-center gap-2"><i className="fa-solid fa-arrow-right-arrow-left w-5 text-center"></i><span className="text-sm">Exchange</span></div>
              <div className="flex items-center gap-2"><i className="fa-solid fa-bell w-5 text-center"></i><span className="text-sm">Alerts</span></div>
              <div className="flex items-center gap-2"><i className="fa-solid fa-gear w-5 text-center"></i><span className="text-sm">Settings</span></div>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Action Icons</h3>
            <Preview title="Common Actions">
              <i className="fa-solid fa-plus" title="Add"></i>
              <i className="fa-solid fa-minus" title="Remove"></i>
              <i className="fa-solid fa-xmark" title="Close"></i>
              <i className="fa-solid fa-check" title="Confirm"></i>
              <i className="fa-solid fa-magnifying-glass" title="Search"></i>
              <i className="fa-solid fa-filter" title="Filter"></i>
              <i className="fa-solid fa-download" title="Download"></i>
              <i className="fa-solid fa-upload" title="Upload"></i>
              <i className="fa-solid fa-ellipsis" title="More"></i>
              <i className="fa-solid fa-eye" title="View"></i>
              <i className="fa-solid fa-eye-slash" title="Hide"></i>
              <i className="fa-solid fa-star" title="Favorite"></i>
              <i className="fa-regular fa-star" title="Unfavorite"></i>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Status Icons</h3>
            <Preview title="Status Indicators">
              <div className="flex items-center gap-2"><i className="fa-solid fa-check text-success"></i><span className="text-sm">Success</span></div>
              <div className="flex items-center gap-2"><i className="fa-solid fa-xmark text-destructive"></i><span className="text-sm">Error</span></div>
              <div className="flex items-center gap-2"><i className="fa-solid fa-triangle-exclamation text-warning"></i><span className="text-sm">Warning</span></div>
              <div className="flex items-center gap-2"><i className="fa-solid fa-circle-info text-info"></i><span className="text-sm">Info</span></div>
              <div className="flex items-center gap-2"><i className="fa-solid fa-clock text-muted-foreground"></i><span className="text-sm">Pending</span></div>
              <div className="flex items-center gap-2"><i className="fa-solid fa-spinner animate-spin text-primary"></i><span className="text-sm">Loading</span></div>
            </Preview>
          </Section>

          {/* Spacing */}
          <Section id="spacing" title="Spacing" description="Consistent spacing scale based on 4px grid.">
            <Preview title="Spacing Scale">
              <div className="w-full space-y-4">
                {[
                  { name: '0.5 (2px)', class: 'w-0.5' },
                  { name: '1 (4px)', class: 'w-1' },
                  { name: '2 (8px)', class: 'w-2' },
                  { name: '3 (12px)', class: 'w-3' },
                  { name: '4 (16px)', class: 'w-4' },
                  { name: '5 (20px)', class: 'w-5' },
                  { name: '6 (24px)', class: 'w-6' },
                  { name: '8 (32px)', class: 'w-8' },
                  { name: '10 (40px)', class: 'w-10' },
                  { name: '12 (48px)', class: 'w-12' },
                ].map((spacing) => (
                  <div key={spacing.name} className="flex items-center gap-4">
                    <span className="text-xs text-muted-foreground w-20">{spacing.name}</span>
                    <div className={cn("h-4 bg-primary rounded", spacing.class)}></div>
                  </div>
                ))}
              </div>
            </Preview>

            <h3 className="text-base lg:text-lg font-medium mb-3 lg:mb-4 mt-6 lg:mt-8">Border Radius</h3>
            <Preview title="Radius Scale">
              <div className="w-12 h-12 bg-primary rounded-none flex items-center justify-center text-primary-foreground text-xs">none</div>
              <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center text-primary-foreground text-xs">sm</div>
              <div className="w-12 h-12 bg-primary rounded flex items-center justify-center text-primary-foreground text-xs">md</div>
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center text-primary-foreground text-xs">lg</div>
              <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center text-primary-foreground text-xs">xl</div>
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-xs">full</div>
            </Preview>
          </Section>

          {/* Footer */}
          <footer className="mt-16 pt-8 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              CryptoX Design System v1.0.0 • Built with React, Tailwind CSS & shadcn/ui
            </p>
          </footer>

        </main>
      </div>
    </div>
  );
};

export default DesignSystem;
